import type {
  CampaignAudienceDto,
  CampaignDto,
  CustomerStage,
  CreateDiscountRequest,
  CreatePromotionRequest,
  DiscountDto,
  DiscountType,
  PromotionDto,
  PromotionScope,
  PromotionType,
  UpdateDiscountRequest,
  PromotionKind,
} from '@ecom/contracts';
import type { AdminLocale } from '@/i18n';
import { halalasToRiyals, riyalsToHalalas } from './price-input';

/**
 * The marketing forms — codes, automatic discounts, campaigns — read into the
 * API's requests.
 *
 * Kept out of the pages so the rules a form has to get right are tested
 * without React: a day typed as `2026-10-31` that means *until the end of the
 * 31st*, a percentage that is a whole number, and an edit that sends only what
 * the operator actually changed.
 */

// ─── Days ────────────────────────────────────────────────────────────────────

/**
 * A `YYYY-MM-DD` from a date input as the first moment of that day, where the
 * operator is. Null for anything that is not a real day.
 */
export function dayStart(day: string): string | null {
  const parts = parseDay(day);
  return parts ? new Date(parts.year, parts.month - 1, parts.day, 0, 0, 0, 0).toISOString() : null;
}

/**
 * The last moment of that day. An end date means "until the end of it": as
 * midnight at its start, a code chosen to end on the 31st would stop on the 30th.
 */
export function dayEnd(day: string): string | null {
  const parts = parseDay(day);
  return parts ? new Date(parts.year, parts.month - 1, parts.day, 23, 59, 59, 999).toISOString() : null;
}

/** An instant back to the day it falls on where the operator is — what a date input shows. */
export function localDay(value: string | null | undefined): string {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

/** "5 Oct 2026", "٥ أكتوبر ٢٠٢٦" — a day, in the reader's language. */
export function dayLabel(value: string, locale: AdminLocale): string {
  return dayFormat(locale).format(new Date(value));
}

/** "Oct 5 – Nov 10, 2026" — the range written the way the language writes ranges. */
export function dayRangeLabel(from: string, to: string, locale: AdminLocale): string {
  return dayFormat(locale).formatRange(new Date(from), new Date(to));
}

/** A whole percent as the language writes it: "25%", "٢٥٪". */
export function percentLabel(percent: number, locale: AdminLocale): string {
  return new Intl.NumberFormat(locale === 'ar' ? 'ar-SA' : 'en-SA', {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(percent / 100);
}

/** "Abayas, Dresses, and Silk" / "عبايات وفساتين وحرير" — the language's own separators. */
export function listOf(items: string[], locale: AdminLocale): string {
  return new Intl.ListFormat(locale, { type: 'conjunction', style: 'long' }).format(items);
}

function dayFormat(locale: AdminLocale): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar-SA-u-ca-gregory' : 'en-SA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function parseDay(day: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day.trim());
  if (!match) return null;

  const [year, month, date] = [Number(match[1]), Number(match[2]), Number(match[3])];
  // A day that does not exist — the 31st of June — rolls over rather than failing; refuse it.
  const probe = new Date(year, month - 1, date);
  return probe.getFullYear() === year && probe.getMonth() === month - 1 && probe.getDate() === date
    ? { year, month, day: date }
    : null;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

// ─── Numbers as typed ────────────────────────────────────────────────────────

/** Arabic-Indic digits and separators, as an Arabic keyboard types them. */
function latin(input: string): string {
  return input
    .trim()
    .replace(/[٠-٩]/g, (digit) => String(digit.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (digit) => String(digit.charCodeAt(0) - 0x06f0))
    .replace(/٫/g, '.')
    .replace(/٬/g, ',');
}

/** A whole number as typed, or null. */
export function wholeNumber(input: string): number | null {
  const text = latin(input);
  if (!/^\d+$/.test(text)) return null;
  const value = Number(text);
  return Number.isSafeInteger(value) ? value : null;
}

/** Riyals as typed → halalas, on the digit string (ADR-004); null when it is not an amount. */
export function amountInput(input: string): number | null {
  return riyalsToHalalas(latin(input));
}

// ─── Results ─────────────────────────────────────────────────────────────────

/**
 * Why a field was refused. The page words it; the code is what a test can
 * assert on without depending on the copy.
 */
export type DraftError =
  | 'required'
  | 'tooShort'
  | 'percent'
  | 'amount'
  | 'whole'
  | 'date'
  | 'endBeforeStart'
  | 'maxBelowMin'
  /** Quantity tiers must climb, each above the one before (ADR-069). */
  | 'tiersAscend'
  /** A gift for spending needs a basket minimum (ADR-069). */
  | 'giftNeedsMinimum'
  /** A set holds two different things at least (ADR-078). */
  | 'setNeedsTwo';

export type DraftResult<T, F extends string> =
  | { ok: true; body: T }
  | { ok: false; errors: Partial<Record<F, DraftError>> };

// ─── Discount codes ──────────────────────────────────────────────────────────

export interface DiscountDraft {
  code: string;
  type: DiscountType;
  /** A whole percent, or riyals for a fixed amount; unused for free shipping. */
  value: string;
  minOrderTotal: string;
  maxUses: string;
  maxUsesPerUser: string;
  /** `YYYY-MM-DD`, as the date input holds it. */
  startsAt: string;
  endsAt: string;
  /** For one customer group (ADR-085); '' for anyone. */
  customerGroupId: string;
  /** Only for a first order (ADR-114). */
  newCustomersOnly: boolean;
}

export type DiscountField = keyof DiscountDraft;

export function discountDraft(discount: DiscountDto | null): DiscountDraft {
  if (!discount) {
    return {
      code: '',
      type: 'PERCENTAGE',
      value: '',
      minOrderTotal: '',
      maxUses: '',
      maxUsesPerUser: '',
      startsAt: '',
      endsAt: '',
      customerGroupId: '',
      newCustomersOnly: false,
    };
  }

  return {
    code: discount.code,
    type: discount.type,
    value:
      discount.type === 'PERCENTAGE'
        ? String(discount.value)
        : discount.type === 'FIXED'
          ? halalasToRiyals(discount.value)
          : '',
    minOrderTotal: discount.minOrderTotal === null ? '' : halalasToRiyals(discount.minOrderTotal),
    maxUses: discount.maxUses === null ? '' : String(discount.maxUses),
    maxUsesPerUser: discount.maxUsesPerUser === null ? '' : String(discount.maxUsesPerUser),
    startsAt: localDay(discount.startsAt),
    endsAt: localDay(discount.endsAt),
    customerGroupId: discount.customerGroupId ?? '',
    newCustomersOnly: discount.newCustomersOnly,
  };
}

/** A code as a shopper types it: Latin letters, digits, hyphen, underscore — what the API accepts. */
export function codeInput(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 60);
}

interface ParsedDiscount {
  code: string;
  value: number;
  minOrderTotal: number | null;
  maxUses: number | null;
  maxUsesPerUser: number | null;
  startsAt: string | null;
  endsAt: string | null;
}

function parseDiscount(draft: DiscountDraft): {
  parsed: ParsedDiscount;
  errors: Partial<Record<DiscountField, DraftError>>;
} {
  const errors: Partial<Record<DiscountField, DraftError>> = {};

  // A percentage is a whole number; a fixed amount is money (ADR-004).
  let value = 0;
  if (draft.type === 'PERCENTAGE') {
    const percent = wholeNumber(draft.value);
    if (percent === null || percent < 1 || percent > 100) errors.value = 'percent';
    else value = percent;
  } else if (draft.type === 'FIXED') {
    const halalas = amountInput(draft.value);
    if (halalas === null || halalas === 0) errors.value = 'amount';
    else value = halalas;
  }

  const minimum = optional(draft.minOrderTotal, amountInput, 'amount', 'minOrderTotal', errors);
  const maxUses = optional(draft.maxUses, positiveWhole, 'whole', 'maxUses', errors);
  const maxUsesPerUser = optional(draft.maxUsesPerUser, positiveWhole, 'whole', 'maxUsesPerUser', errors);
  const startsAt = optional(draft.startsAt, dayStart, 'date', 'startsAt', errors);
  const endsAt = optional(draft.endsAt, dayEnd, 'date', 'endsAt', errors);

  return {
    parsed: {
      code: draft.code.trim().toUpperCase(),
      value,
      // Zero is no minimum, and is stored as none.
      minOrderTotal: minimum ? minimum : null,
      maxUses,
      maxUsesPerUser,
      startsAt,
      endsAt,
    },
    errors,
  };
}

export function discountCreate(draft: DiscountDraft): DraftResult<CreateDiscountRequest, DiscountField> {
  const { parsed, errors } = parseDiscount(draft);
  if (!parsed.code) errors.code = 'required';
  if (!errors.endsAt && endsBeforeStart(parsed.startsAt, parsed.endsAt)) errors.endsAt = 'endBeforeStart';
  if (Object.keys(errors).length > 0) return { ok: false, errors };

  return {
    ok: true,
    body: {
      code: parsed.code,
      type: draft.type,
      value: parsed.value,
      minOrderTotal: parsed.minOrderTotal,
      maxUses: parsed.maxUses,
      maxUsesPerUser: parsed.maxUsesPerUser,
      startsAt: parsed.startsAt,
      endsAt: parsed.endsAt,
      customerGroupId: draft.customerGroupId || null,
      // Said only when on, so a plain code reads as before.
      ...(draft.newCustomersOnly ? { newCustomersOnly: true } : {}),
    },
  };
}

/**
 * An edit, as only what changed — the code and its type stay as created. A
 * field emptied is sent as null, which the API reads as "no date", "no limit".
 */
export function discountUpdate(
  draft: DiscountDraft,
  original: DiscountDto,
): DraftResult<UpdateDiscountRequest, DiscountField> {
  const { parsed, errors } = parseDiscount(draft);
  const body: UpdateDiscountRequest = {};
  const before = discountDraft(original);

  if (original.type !== 'FREE_SHIPPING' && !errors.value && parsed.value !== original.value) body.value = parsed.value;
  // A stored zero and an empty field are the same "no minimum".
  if (!errors.minOrderTotal && (parsed.minOrderTotal ?? 0) !== (original.minOrderTotal ?? 0)) {
    body.minOrderTotal = parsed.minOrderTotal;
  }
  if (!errors.maxUsesPerUser && parsed.maxUsesPerUser !== original.maxUsesPerUser) {
    body.maxUsesPerUser = parsed.maxUsesPerUser;
  }

  if (!errors.maxUses && parsed.maxUses !== original.maxUses) body.maxUses = parsed.maxUses;
  if ((draft.customerGroupId || null) !== original.customerGroupId) body.customerGroupId = draft.customerGroupId || null;
  if (draft.newCustomersOnly !== original.newCustomersOnly) body.newCustomersOnly = draft.newCustomersOnly;

  // Compared as typed days: a stored instant that was never touched is not re-snapped.
  if (!errors.startsAt && draft.startsAt !== before.startsAt) body.startsAt = parsed.startsAt;
  if (!errors.endsAt && draft.endsAt !== before.endsAt) body.endsAt = parsed.endsAt;

  const start = body.startsAt ?? original.startsAt;
  const end = body.endsAt ?? original.endsAt;
  if (!errors.endsAt && !errors.startsAt && endsBeforeStart(start, end)) errors.endsAt = 'endBeforeStart';

  return Object.keys(errors).length > 0 ? { ok: false, errors } : { ok: true, body };
}

// ─── Automatic discounts ─────────────────────────────────────────────────────

export interface PromotionDraft {
  /** Staff-facing; shoppers see it only where `shopperName` has no words. */
  name: string;
  shopperName: { ar: string; en: string };
  type: PromotionType;
  value: string;
  scope: PromotionScope;
  targetIds: string[];
  minOrderTotal: string;
  minQuantity: string;
  maxDiscount: string;
  priority: string;
  stacksWithCoupons: boolean;
  startsAt: string;
  endsAt: string;
  /** A discount on lines, or buy X get Y (ADR-069). */
  kind: PromotionKind;
  /** DISCOUNT: "2 for 10%, 3 for 15%" — empty means the flat value. */
  tiered: boolean;
  tiers: { minQuantity: string; value: string }[];
  /** BUY_X_GET_Y: "0" is a gift for spending. */
  buyQuantity: string;
  getQuantity: string;
  getPercent: string;
  /** '' = the same products as the offer's. */
  getScope: '' | RewardScope;
  getTargetIds: string[];
  maxApplications: string;
  /** BUNDLE: the set's things; `value` is the set's price in riyals (ADR-078). */
  items: { variantId: string; label: string; quantity: string }[];
}

type RewardScope = Exclude<PromotionScope, 'ORDER'>;

export type PromotionField = keyof PromotionDraft;

export function promotionDraft(promotion: PromotionDto | null): PromotionDraft {
  if (!promotion) {
    return {
      name: '',
      shopperName: { ar: '', en: '' },
      type: 'PERCENTAGE',
      value: '',
      scope: 'ALL',
      targetIds: [],
      minOrderTotal: '',
      minQuantity: '',
      maxDiscount: '',
      priority: '0',
      stacksWithCoupons: true,
      startsAt: '',
      endsAt: '',
      kind: 'DISCOUNT',
      tiered: false,
      tiers: [
        { minQuantity: '2', value: '' },
        { minQuantity: '3', value: '' },
      ],
      buyQuantity: '1',
      getQuantity: '1',
      getPercent: '100',
      getScope: '',
      getTargetIds: [],
      maxApplications: '',
      items: [],
    };
  }

  return {
    name: promotion.name,
    shopperName: { ar: promotion.translations?.ar ?? '', en: promotion.translations?.en ?? '' },
    type: promotion.type,
    value: promotion.type === 'PERCENTAGE' && promotion.kind !== 'BUNDLE' ? String(promotion.value) : halalasToRiyals(promotion.value),
    scope: promotion.scope,
    targetIds: promotion.targetIds,
    minOrderTotal: promotion.minOrderTotal ? halalasToRiyals(promotion.minOrderTotal) : '',
    minQuantity: promotion.minQuantity === null ? '' : String(promotion.minQuantity),
    maxDiscount: promotion.maxDiscount === null ? '' : halalasToRiyals(promotion.maxDiscount),
    priority: String(promotion.priority),
    stacksWithCoupons: promotion.stacksWithCoupons,
    startsAt: localDay(promotion.startsAt),
    endsAt: localDay(promotion.endsAt),
    kind: promotion.kind,
    tiered: promotion.tiers.length > 0,
    tiers:
      promotion.tiers.length > 0
        ? promotion.tiers.map((tier) => ({
            minQuantity: String(tier.minQuantity),
            value: promotion.type === 'PERCENTAGE' ? String(tier.value) : halalasToRiyals(tier.value),
          }))
        : [
            { minQuantity: '2', value: '' },
            { minQuantity: '3', value: '' },
          ],
    buyQuantity: promotion.buyQuantity === null ? '1' : String(promotion.buyQuantity),
    getQuantity: promotion.getQuantity === null ? '1' : String(promotion.getQuantity),
    getPercent: promotion.getPercent === null ? '100' : String(promotion.getPercent),
    getScope: promotion.getScope ?? '',
    getTargetIds: promotion.getTargetIds,
    maxApplications: promotion.maxApplications === null ? '' : String(promotion.maxApplications),
    items: (promotion.items ?? []).map((item) => ({
      variantId: item.variantId,
      label: [item.productName, item.sku].filter(Boolean).join(' · '),
      quantity: String(item.quantity),
    })),
  };
}

export type PromotionState = 'running' | 'scheduled' | 'ended' | 'stopped';

/**
 * One word for where it stands. "Running" is the API's own answer; the rest
 * splits "switched on but not running" into the two things it can mean —
 * not started yet, or over — because one label for both gets misread.
 */
export function promotionState(
  promotion: Pick<PromotionDto, 'isActive' | 'isRunning' | 'startsAt'>,
  now = Date.now(),
): PromotionState {
  if (!promotion.isActive) return 'stopped';
  if (promotion.isRunning) return 'running';
  if (promotion.startsAt && new Date(promotion.startsAt).getTime() > now) return 'scheduled';
  return 'ended';
}

/** Scopes that pick products or categories; the others apply to the basket as a whole. */
export function scopeHasTargets(scope: PromotionScope): boolean {
  return scope === 'CATEGORY' || scope === 'PRODUCT';
}

/**
 * The request a promotion form makes, or why it cannot. On an edit the dates
 * go only when changed, so a stored instant nobody touched is not re-snapped
 * to the start or end of its day.
 */
export function promotionRequest(
  draft: PromotionDraft,
  original: PromotionDto | null,
): DraftResult<CreatePromotionRequest, PromotionField> {
  const errors: Partial<Record<PromotionField, DraftError>> = {};

  const name = draft.name.trim();
  if (!name) errors.name = 'required';

  const reward = draft.kind === 'BUY_X_GET_Y';
  // A set at its own price (ADR-078): its things and its price, nothing else.
  const bundle = draft.kind === 'BUNDLE';
  const tiered = !reward && !bundle && draft.tiered;

  /** One value as the offer's type reads it: a percent, or halalas from riyals typed. */
  const valueOf = (typed: string): number | null => {
    if (draft.type === 'PERCENTAGE') {
      const percent = wholeNumber(typed);
      return percent === null || percent < 1 || percent > 100 ? null : percent;
    }
    const halalas = amountInput(typed);
    return halalas === null || halalas === 0 ? null : halalas;
  };

  let value = 0;
  if (bundle) {
    const price = amountInput(draft.value);
    if (price === null || price === 0) errors.value = 'amount';
    else value = price;
  } else if (!reward && !tiered) {
    const typed = valueOf(draft.value);
    if (typed === null) errors.value = draft.type === 'PERCENTAGE' ? 'percent' : 'amount';
    else value = typed;
  }

  // Quantity tiers (ADR-069): each row read, then checked to climb.
  const tiers: { minQuantity: number; value: number }[] = [];
  if (tiered) {
    for (const row of draft.tiers) {
      const minQuantity = positiveWhole(row.minQuantity);
      const tierValue = valueOf(row.value);
      if (minQuantity === null || tierValue === null) {
        errors.tiers = draft.type === 'PERCENTAGE' ? 'percent' : 'amount';
        break;
      }
      if (tiers.length > 0 && minQuantity <= tiers[tiers.length - 1]!.minQuantity) {
        errors.tiers = 'tiersAscend';
        break;
      }
      tiers.push({ minQuantity, value: tierValue });
    }
    if (!errors.tiers && tiers.length === 0) errors.tiers = 'required';
    if (!errors.tiers) value = tiers[0]!.value;
  }

  if (!bundle && scopeHasTargets(draft.scope) && draft.targetIds.length === 0) errors.targetIds = 'required';

  // A set's things: two at least, each once, each a whole number of it.
  const items: { variantId: string; quantity: number }[] = [];
  if (bundle) {
    for (const row of draft.items.filter((entry) => entry.variantId)) {
      const quantity = positiveWhole(row.quantity);
      if (quantity === null || quantity > 99) errors.items = 'whole';
      else items.push({ variantId: row.variantId, quantity });
    }
    if (!errors.items && (items.length < 2 || new Set(items.map((item) => item.variantId)).size !== items.length)) errors.items = 'setNeedsTwo';
  }

  // Buy X get Y (ADR-069): how many, the reward, and where it comes from.
  const buyQuantity = reward ? wholeNumber(draft.buyQuantity) : null;
  const getQuantity = reward ? positiveWhole(draft.getQuantity) : null;
  const getPercent = reward ? wholeNumber(draft.getPercent) : null;
  if (reward) {
    if (buyQuantity === null) errors.buyQuantity = 'whole';
    if (getQuantity === null) errors.getQuantity = 'whole';
    if (getPercent === null || getPercent < 1 || getPercent > 100) errors.getPercent = 'percent';
    if (draft.getScope && scopeHasTargets(draft.getScope) && draft.getTargetIds.length === 0) errors.getTargetIds = 'required';
  }
  const applications = reward || bundle ? optional(draft.maxApplications, positiveWhole, 'whole', 'maxApplications', errors) : null;

  const minimum = optional(draft.minOrderTotal, amountInput, 'amount', 'minOrderTotal', errors);
  const quantity = optional(draft.minQuantity, positiveWhole, 'whole', 'minQuantity', errors);
  const cap = optional(draft.maxDiscount, amountInput, 'amount', 'maxDiscount', errors);
  const priority = draft.priority.trim() === '' ? 0 : wholeNumber(draft.priority);
  if (priority === null) errors.priority = 'whole';
  // A gift with nothing to buy needs a basket to earn it.
  if (reward && buyQuantity === 0 && !errors.minOrderTotal && !minimum) errors.minOrderTotal = 'giftNeedsMinimum';

  const startsAt = optional(draft.startsAt, dayStart, 'date', 'startsAt', errors);
  const endsAt = optional(draft.endsAt, dayEnd, 'date', 'endsAt', errors);
  const before = original ? promotionDraft(original) : null;
  const startChanged = !before || draft.startsAt !== before.startsAt;
  const endChanged = !before || draft.endsAt !== before.endsAt;
  const start = startChanged ? startsAt : (original?.startsAt ?? null);
  const end = endChanged ? endsAt : (original?.endsAt ?? null);
  if (!errors.startsAt && !errors.endsAt && endsBeforeStart(start, end)) errors.endsAt = 'endBeforeStart';

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const shopperName = { ar: draft.shopperName.ar.trim(), en: draft.shopperName.en.trim() };

  return {
    ok: true,
    body: {
      name,
      // Sent empty on an edit so wording can be taken away; left out of a new one.
      ...(original || shopperName.ar || shopperName.en ? { translations: shopperName } : {}),
      // A set is a price for its things; the API takes its products for targets (ADR-078).
      type: bundle ? 'FIXED' : draft.type,
      value,
      scope: bundle ? 'PRODUCT' : draft.scope,
      // The whole order and everything carry no targets.
      targetIds: !bundle && scopeHasTargets(draft.scope) ? draft.targetIds : [],
      minOrderTotal: minimum ? minimum : null,
      // "Buy one or more" is no condition at all — nor one for buy X get Y, whose X is its own.
      minQuantity: !reward && !bundle && quantity !== null && quantity > 1 ? quantity : null,
      maxDiscount: cap && !bundle ? cap : null,
      priority: priority ?? 0,
      stacksWithCoupons: draft.stacksWithCoupons,
      ...(startChanged ? { startsAt } : {}),
      ...(endChanged ? { endsAt } : {}),
      kind: draft.kind,
      tiers,
      buyQuantity: reward ? buyQuantity : null,
      getQuantity: reward ? getQuantity : null,
      getPercent: reward ? getPercent : null,
      getScope: reward && draft.getScope ? draft.getScope : null,
      getTargetIds: reward && draft.getScope && scopeHasTargets(draft.getScope) ? draft.getTargetIds : [],
      maxApplications: reward || bundle ? applications : null,
      ...(bundle ? { items } : {}),
    },
  };
}

// ─── Campaigns ───────────────────────────────────────────────────────────────

export type CampaignChannel = CampaignDto['channel'];

export interface CampaignDraft {
  name: string;
  channel: CampaignChannel;
  subject: { ar: string; en: string };
  body: { ar: string; en: string };
  city: string;
  hasNeverOrdered: boolean;
  minOrders: string;
  /** At most this many sold orders; 1 with at least 1 is "bought once, never again" (ADR-065). */
  maxOrders: string;
  /** `YYYY-MM-DD`: customers who joined on or after it. */
  registeredAfter: string;
  /** Carries any of these (ADR-064). */
  tags: string[];
  abandonedCart: boolean;
  wishlistPriceDrop: boolean;
  boughtProductId: string;
  boughtCategoryId: string;
  /** Members of a customer group (ADR-085). */
  groupId: string;
  /** In any of these stages on the day it sends (ADR-103). */
  stages: CustomerStage[];
  /** WhatsApp (ADR-106): the store's approved marketing template — the message itself. */
  whatsappTemplate: { name: string; language: string } | null;
  /** Email (ADR-113): try a second subject on a slice of the audience first. */
  abTest: boolean;
  subjectB: { ar: string; en: string };
  /** Share of the audience tested, 10–50, split evenly between the two subjects. */
  testPercent: number;
  /** Hours to wait before the winner goes to the rest. */
  testHours: number;
}

export type CampaignField = keyof CampaignDraft;

/** The test shares and waits the form offers (ADR-113). */
export const AB_TEST_SHARES = [10, 20, 30, 40, 50] as const;
export const AB_TEST_WAITS = [1, 2, 4, 8, 24, 48] as const;

export interface CampaignRequest {
  name: string;
  channel: CampaignChannel;
  subject?: { ar: string; en: string };
  /** Not for WhatsApp: the template is the message. */
  body?: { ar: string; en: string };
  whatsappTemplate?: { name: string; language: string };
  audience: CampaignAudienceDto;
  /** Email only; null takes a test off (ADR-113). */
  abTest?: { subjectB: { ar: string; en: string }; testPercent: number; testHours: number } | null;
}

export function campaignDraft(campaign: CampaignDto | null): CampaignDraft {
  return {
    name: campaign?.name ?? '',
    channel: campaign?.channel ?? 'EMAIL',
    subject: { ar: campaign?.subject?.ar ?? '', en: campaign?.subject?.en ?? '' },
    body: { ar: campaign?.body.ar ?? '', en: campaign?.body.en ?? '' },
    city: campaign?.audience.city ?? '',
    hasNeverOrdered: campaign?.audience.hasNeverOrdered ?? false,
    minOrders: campaign?.audience.minOrders ? String(campaign.audience.minOrders) : '',
    maxOrders: campaign?.audience.maxOrders !== undefined ? String(campaign.audience.maxOrders) : '',
    registeredAfter: localDay(campaign?.audience.registeredAfter),
    tags: campaign?.audience.tags ?? [],
    abandonedCart: campaign?.audience.abandonedCart ?? false,
    wishlistPriceDrop: campaign?.audience.wishlistPriceDrop ?? false,
    boughtProductId: campaign?.audience.boughtProductId ?? '',
    boughtCategoryId: campaign?.audience.boughtCategoryId ?? '',
    groupId: campaign?.audience.groupId ?? '',
    stages: campaign?.audience.stages ?? [],
    whatsappTemplate: campaign?.whatsappTemplate ? { name: campaign.whatsappTemplate.name, language: campaign.whatsappTemplate.language } : null,
    abTest: Boolean(campaign?.abTest),
    subjectB: { ar: campaign?.abTest?.subjectB.ar ?? '', en: campaign?.abTest?.subjectB.en ?? '' },
    testPercent: campaign?.abTest?.testPercent ?? 20,
    testHours: campaign?.abTest?.testHours ?? 4,
  };
}

/**
 * Who it goes to, with nothing for a filter left empty. Consent is not in
 * here: the API adds it to every audience, always.
 */
export function campaignAudience(draft: CampaignDraft): CampaignAudienceDto {
  const minOrders = wholeNumber(draft.minOrders);
  const maxOrders = draft.maxOrders.trim() ? wholeNumber(draft.maxOrders) : null;
  const registeredAfter = dayStart(draft.registeredAfter);

  return {
    ...(draft.city.trim() ? { city: draft.city.trim() } : {}),
    ...(draft.hasNeverOrdered ? { hasNeverOrdered: true } : {}),
    ...(minOrders ? { minOrders } : {}),
    ...(maxOrders !== null ? { maxOrders } : {}),
    ...(registeredAfter ? { registeredAfter } : {}),
    ...(draft.tags.length > 0 ? { tags: draft.tags } : {}),
    ...(draft.abandonedCart ? { abandonedCart: true } : {}),
    ...(draft.wishlistPriceDrop ? { wishlistPriceDrop: true } : {}),
    ...(draft.boughtProductId ? { boughtProductId: draft.boughtProductId } : {}),
    ...(draft.boughtCategoryId ? { boughtCategoryId: draft.boughtCategoryId } : {}),
    ...(draft.groupId ? { groupId: draft.groupId } : {}),
    ...(draft.stages.length > 0 ? { stages: draft.stages } : {}),
  };
}

export function campaignRequest(draft: CampaignDraft): DraftResult<CampaignRequest, CampaignField> {
  const errors: Partial<Record<CampaignField, DraftError>> = {};
  const name = draft.name.trim();
  const subject = { ar: draft.subject.ar.trim(), en: draft.subject.en.trim() };
  const body = { ar: draft.body.ar.trim(), en: draft.body.en.trim() };

  if (!name) errors.name = 'required';
  else if (name.length < 2) errors.name = 'tooShort';
  // WhatsApp sends the store's approved template, not words typed here (ADR-106).
  if (draft.channel === 'WHATSAPP') {
    if (!draft.whatsappTemplate) errors.whatsappTemplate = 'required';
  } else if (!body.ar && !body.en) errors.body = 'required';
  if (draft.channel === 'EMAIL' && !subject.ar && !subject.en) errors.subject = 'required';
  const subjectB = { ar: draft.subjectB.ar.trim(), en: draft.subjectB.en.trim() };
  if (draft.channel === 'EMAIL' && draft.abTest && !subjectB.ar && !subjectB.en) errors.subjectB = 'required';
  if (draft.minOrders.trim() && positiveWhole(draft.minOrders) === null) errors.minOrders = 'whole';
  if (draft.maxOrders.trim() && wholeNumber(draft.maxOrders) === null) errors.maxOrders = 'whole';
  else if ((wholeNumber(draft.maxOrders) ?? Infinity) < (wholeNumber(draft.minOrders) ?? 0)) errors.maxOrders = 'maxBelowMin';
  if (draft.registeredAfter.trim() && dayStart(draft.registeredAfter) === null) errors.registeredAfter = 'date';

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  return {
    ok: true,
    body: {
      name,
      channel: draft.channel,
      // A text has no subject line.
      ...(draft.channel === 'EMAIL' ? { subject } : {}),
      ...(draft.channel === 'WHATSAPP' && draft.whatsappTemplate ? { whatsappTemplate: draft.whatsappTemplate } : { body }),
      audience: campaignAudience(draft),
      // A second subject tried first (ADR-113); said only when there is one, so other campaigns read as before.
      ...(draft.channel === 'EMAIL' && draft.abTest ? { abTest: { subjectB, testPercent: draft.testPercent, testHours: draft.testHours } } : {}),
    },
  };
}

// ─── Shared ──────────────────────────────────────────────────────────────────

function positiveWhole(input: string): number | null {
  const value = wholeNumber(input);
  return value !== null && value >= 1 ? value : null;
}

/** An optional field: empty is null, anything else must read or is refused. */
function optional<F extends string, T>(
  input: string,
  read: (input: string) => T | null,
  error: DraftError,
  field: F,
  errors: Partial<Record<F, DraftError>>,
): T | null {
  if (input.trim() === '') return null;
  const value = read(input);
  if (value === null) errors[field] = error;
  return value;
}

function endsBeforeStart(start: string | null, end: string | null): boolean {
  return Boolean(start && end && new Date(end).getTime() <= new Date(start).getTime());
}
